import React, { useState } from "react";
import truncate from "lodash/truncate";
import { useMutation } from "@apollo/client";

import {
  Card,
  CardActionArea,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Star, StarBorder } from "@mui/icons-material";

import { grayLight, grayMedium, alabaster } from "design-system/colors";
import CircleAvatar from "design-system/components/CircleAvatar";
import { useScreenSize, useSnackbar } from "globals/hooks";
import { Contact } from "types";
import { SET_PREFERRED_PASSENGER_MUTATION } from "globals/graphql/contact.graphql";
import { LOAD_ME_QUERY } from "globals/graphql/authenticatedContact.graphql";

type PassengerListItemProps = {
  passenger: Contact;
  onPassengerClick: (passenger: Contact) => void;
  isPreferred: boolean;
};

function PassengerListItem(props: PassengerListItemProps) {
  const { passenger, onPassengerClick, isPreferred } = props;

  const { firstName, lastName, email, mobilePhone, id } = passenger;

  // hooks
  const { isMobileView } = useScreenSize();
  const snackbar = useSnackbar();

  // state
  const [isHovered, setIsHovered] = useState(false);

  // mutations
  const [setPreferredPassenger, { loading }] = useMutation(
    SET_PREFERRED_PASSENGER_MUTATION,
    {
      refetchQueries: [{ query: LOAD_ME_QUERY }],
      onCompleted(data) {
        const result = data?.setPreferredPassenger?.result;
        if (result?.message) {
          snackbar.error(result.message);
        } else if (result?.contact) {
          snackbar.success(
            result.contact.preferredPassenger
              ? "Preferred passenger set"
              : "Preferred passenger removed"
          );
        }
      },
      onError() {
        snackbar.error("Failed to update preferred passenger");
      },
    }
  );

  // event handlers
  const handleStarClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    // If already preferred, clear preference. Otherwise, set as preferred.
    const passengerId = isPreferred ? null : id;

    setPreferredPassenger({
      variables: {
        input: { passengerId },
      },
    });
  };

  const showStar = isMobileView || isHovered || isPreferred;

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          marginBottom: 1.5,
          borderColor: grayLight,
          "&:hover": {
            boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.06)",
            borderColor: grayMedium,
          },
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <CardActionArea
          onClick={() => onPassengerClick(passenger)}
          sx={{
            ".MuiCardActionArea-focusHighlight": { background: "transparent" },
          }}
        >
          <Box
            py={3}
            display="flex"
            flexDirection={isMobileView ? "column" : "row"}
            alignItems={isMobileView ? "start" : "center"}
          >
            {/* Name and Avatar */}
            <Box display="flex" flex="4">
              <Box ml={1.5} mr={1.3}>
                <CircleAvatar
                  placeholder={[firstName, lastName]}
                  size="small"
                />
              </Box>
              <Box alignSelf="center">
                <Typography variant={isMobileView ? "h4" : "subtitle1"}>
                  {truncate(`${firstName} ${lastName}`)}
                </Typography>
              </Box>
            </Box>

            {/* Email */}
            <Box
              flex="1.5"
              ml={isMobileView ? 2 : 0}
              mt={isMobileView ? 2 : 0}
              mb={isMobileView ? 1 : 0}
            >
              <Typography variant={isMobileView ? "body2" : "caption"}>
                {truncate(email)}
              </Typography>
            </Box>
            {/* Phone Number */}
            <Box flex="1" ml={isMobileView ? 2 : 0}>
              <Typography variant={isMobileView ? "body2" : "caption"}>
                {mobilePhone}
              </Typography>
            </Box>

            {/* Preferred Star */}
            <Box
              flex="0.5"
              display="flex"
              justifyContent="center"
              sx={{
                opacity: showStar ? 1 : 0,
                transition: "opacity 0.2s ease-in-out",
              }}
            >
              <Tooltip
                title={
                  isPreferred
                    ? "Remove as preferred passenger"
                    : "Set as preferred passenger"
                }
                placement="top"
              >
                <IconButton
                  onClick={handleStarClick}
                  disabled={loading}
                  size="small"
                  sx={{
                    "&:hover": {
                      backgroundColor: alabaster,
                    },
                  }}
                >
                  {isPreferred ? (
                    <Star color="primary" />
                  ) : (
                    <StarBorder color="primary" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardActionArea>
      </Card>
    </>
  );
}

export default PassengerListItem;
